import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { SecondaryButton } from "src/ui/shared/CustomButton";
import { copyToClipboard } from "src/ui/utils/clipboard";
import { useStreamingLog } from "src/ui/utils/useStream";

import { Product } from "@dashboard/products/types";
import { LogStreamModal, StreamStatus } from "@shared/LogStreamModal";

import { buildLogLink, getReasonMessage, onDownloadLog, onStartStreaming } from "./LogStreamer.utils";

interface Props {
    buildId: number;
    hasLogs: boolean;
    disabled?: boolean;
    product?: Product;
    workspaceGuid?: string;
}

export const LogStreamer = (props: Props): React.JSX.Element => {
    const [logStreamerChoice, setLogStreamerChoice] = React.useState<boolean | null>(null);
    const [downloading, setDownloading] = React.useState(false);
    const [status, setStatus] = React.useState<StreamStatus>("initializing");

    const [params] = useSearchParams();
    const logParam = params.get("log");
    const [seenLogParam, setSeenLogParam] = React.useState(logParam);

    const { t } = useTranslation(["modals", "common"]);

    const { product, workspaceGuid } = props;
    const onCopyLink =
        product && workspaceGuid
            ? () =>
                  void copyToClipboard(
                      buildLogLink({
                          buildId: props.buildId,
                          codename: product.codename,
                          androidVersion: product.androidVersion,
                          workspaceGuid,
                      }),
                  )
            : undefined;

    const { containerRef, scrollContainerRef, onClearStream, onRunStream, hasContent } = useStreamingLog({
        onStartStreaming: () => onStartStreaming(props.buildId),
        onStatusChange: setStatus,
    });

    const downloadLog = async () => {
        setDownloading(true);
        await onDownloadLog(props.buildId);
        setDownloading(false);
    };

    const openedByLink = logParam === String(props.buildId);

    // Arriving at this build's log link (including via browser Back) reopens the dialog even if it was closed under the
    // same link before, since the layout effect below starts the stream on every such arrival.
    if (logParam !== seenLogParam) {
        setSeenLogParam(logParam);
        if (openedByLink) {
            setLogStreamerChoice(null);
        }
    }

    const logStreamerOpen = logStreamerChoice ?? openedByLink;

    const onOpen = () => {
        setLogStreamerChoice(true);
        onRunStream();
    };

    const onClose = () => {
        setLogStreamerChoice(false);
        onClearStream();
    };

    React.useLayoutEffect(() => {
        if (openedByLink) {
            onRunStream();
        }
        // Depending on `onRunStream` would restart the stream on every render, since it is rebuilt each time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.buildId, logParam]);

    return (
        <React.Fragment>
            <SecondaryButton
                onClick={onOpen}
                disabled={!props.hasLogs || props.disabled}
                tooltipProps={{ title: getReasonMessage(props.hasLogs) }}
            >
                <i className="fa-solid fa-align-left" />
            </SecondaryButton>

            <LogStreamModal
                status={status}
                downloadDisabled={!props.hasLogs}
                open={logStreamerOpen}
                onClose={onClose}
                title={t("modals:logStreamer.buildLogsTitle", { id: props.buildId })}
                onDownload={downloadLog}
                onCopyLink={onCopyLink}
                downloading={downloading}
                containerRef={scrollContainerRef}
                contentRef={containerRef}
                hasContent={hasContent}
            />
        </React.Fragment>
    );
};
